// ---------------------------------------------------------------------------
// ID Card Studio — constants
// ---------------------------------------------------------------------------
// A self-contained module (own constants, data, service, utils, components,
// pages) that designs printable ID-card TEMPLATES and generates cards for
// students, teachers and staff. Templates hold freely positioned elements —
// static text, DYNAMIC person fields (name, class, roll…), the person photo,
// logos and shapes — laid out with drag & drop, then filled per person and
// printed.
//
// Geometry is stored in LOGICAL units where 1 unit = 0.1 mm, so a template maps
// to a real physical card size on print regardless of screen size.
// ---------------------------------------------------------------------------
import {BASE_URL} from "../../../../constants/ApiConstant";

const ID_CARD = "/id-card";

export const idCardApiConstants = {
    LIST_TEMPLATES: BASE_URL.concat(ID_CARD).concat("/template"),
    SAVE_TEMPLATE: BASE_URL.concat(ID_CARD).concat("/template"),
    LIST_PEOPLE: BASE_URL.concat(ID_CARD).concat("/people"),
};

export const STORAGE_KEYS = {
    TEMPLATES: "idc.templates.v1",
    ACTIVE_TEMPLATE: "idc.activeTemplateId",
};

// mm → logical units. 1mm = 10 units. Used by print sizing.
export const UNITS_PER_MM = 10;
// Physical print density (CSS px per mm at 96dpi) for the print sheet.
export const PRINT_PX_PER_MM = 3.7795;

export const ELEMENT_TYPES = {
    FIELD: "field", // dynamic — resolves from the person being printed
    TEXT: "text", // static caption
    PHOTO: "photo", // the person's photo
    IMAGE: "image", // static image (school logo, watermark)
    SHAPE: "shape", // bands / dividers / accents
};

export const PERSON_ROLES = [
    {id: "student", label: "Student"},
    {id: "teacher", label: "Teacher"},
    {id: "staff", label: "Staff"},
];

// Standard ID-card stock sizes. w/h are in LOGICAL units (0.1 mm).
export const CARD_SIZES = [
    {id: "cr80-portrait", label: "Standard Card · Portrait (54 × 86 mm)", w: 540, h: 860},
    {id: "cr80-landscape", label: "Standard Card · Landscape (86 × 54 mm)", w: 860, h: 540},
    {id: "id-portrait", label: "Large ID · Portrait (64 × 89 mm)", w: 640, h: 890},
    {id: "square", label: "Square Badge (60 × 60 mm)", w: 600, h: 600},
];

// The dynamic fields a template element can bind to. `sample` powers the live
// preview inside the designer before any real roster is attached.
export const FIELD_DEFS = [
    {key: "name", label: "Full Name", sample: "Aarav Sharma"},
    {key: "role", label: "Role", sample: "Student"},
    {key: "idNo", label: "ID Number", sample: "GPS-2024-0345"},
    {key: "class", label: "Class / Grade", sample: "Grade 8"},
    {key: "section", label: "Section", sample: "A"},
    {key: "roll", label: "Roll No.", sample: "12"},
    {key: "dob", label: "Date of Birth", sample: "2011-05-14"},
    {key: "blood", label: "Blood Group", sample: "B+"},
    {key: "phone", label: "Phone", sample: "+977 98XXXXXXXX"},
    {key: "guardian", label: "Guardian", sample: "Mr. Ramesh Sharma"},
    {key: "address", label: "Address", sample: "Lalitpur, Nepal"},
    {key: "designation", label: "Designation", sample: "Senior Teacher"},
    {key: "email", label: "Email", sample: "aarav@example.com"},
    {key: "session", label: "Session / Year", sample: "2024 / 2025"},
];

export const COLOR_SWATCHES = [
    "#1A202C", "#2D3748", "#3182CE", "#2B6CB0", "#0B2545",
    "#38A169", "#DD6B20", "#E53E3E", "#805AD5", "#D69E2E",
    "#319795", "#FFFFFF", "#F7FAFC", "#EDF2F7", "#000000",
];

export const FONT_FAMILIES = [
    "Roboto, sans-serif",
    "'Open Sans', sans-serif",
    "Raleway, sans-serif",
    "Georgia, serif",
    "'Courier New', monospace",
];

export const CARD_SIDES = ["front", "back"];
